/**
 * OnboardingTour.jsx — "How Orella works", four screens, once (s95 sign-up rebuild).
 *
 * Dr. Rami (2026-10-04): the explainer runs after sign-up and before the patient
 * uses the app — not in front of sign-up, and with no separate title screen. Each
 * screen is a photo, "How Orella works · n of 4", a headline and one sentence.
 * It only ever runs inside the onboarding finish, so it is shown once per account
 * without a stored flag. Skip and the last button both start the first case.
 */
import { useState } from 'react'
import { useI18n } from '../../i18n'
import { OnboardingShell, PrimaryButton, Footer, useScrollToTop, NAVY, TEXT_MUTED } from './onboardingPrimitives'
import tourDescribe from '../../assets/onboarding/tour-describe.jpg'
import tourShare from '../../assets/onboarding/tour-share.jpg'
import tourVisit from '../../assets/onboarding/tour-visit.jpg'
import tourPlan from '../../assets/onboarding/tour-plan.jpg'

export default function OnboardingTour({ onFinish }) {
  const { t } = useI18n()
  const [i, setI] = useState(0)
  useScrollToTop(`tour-${i}`)

  const steps = [
    { img: tourDescribe, title: t('onboarding.tour.describeTitle', "Describe what's going on"),
      body: t('onboarding.tour.describeBody', "Orella will help you fully describe the issue. We'll suggest photos or records if you have them.") },
    { img: tourShare, title: t('onboarding.tour.shareTitle', 'Share it with your doctor'),
      body: t('onboarding.tour.shareBody', 'Find and select your doctor, and send your case to the office directly.') },
    { img: tourVisit, title: t('onboarding.tour.visitTitle', 'Start the visit in detail'),
      body: t('onboarding.tour.visitBody', 'Your doctor already knows your story, so the time goes to answers.') },
    { img: tourPlan, title: t('onboarding.tour.planTitle', 'Leave with a diagnosis and a plan'),
      body: t('onboarding.tour.planBody', 'Your plan, results and follow-ups stay here in Orella, with full explanations.') },
  ]
  const s = steps[i]
  const last = i === steps.length - 1

  return (
    <OnboardingShell>
      {/* PROD-1195: Skip sits 14px / 18px inside the photo's top-right corner. The frame
          (OnboardingFlow's OnboardingFrame) already pays the status-bar inset above this box, so
          the pill must not pay it again: with env(safe-area-inset-top) in its top it landed ~60px
          down, in the middle of the photo. 58dvh, not 58vh: the visible height on iPhone Safari. */}
      <div data-tour-photo style={{ position: 'relative', width: '100%', aspectRatio: '4 / 5', maxHeight: '58dvh', overflow: 'hidden', flex: 'none' }}>
        <img src={s.img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        {!last && (
          <button
            type="button"
            onClick={onFinish}
            data-tour-skip
            style={{ position: 'absolute', top: 14, right: 18, background: 'rgba(21,53,79,0.35)',
              border: 0, borderRadius: 999, padding: '6px 14px', color: '#fff', fontSize: 14.5, fontWeight: 600 }}
          >
            {t('onboarding.tour.skip', 'Skip')}
          </button>
        )}
      </div>
      <div className="flex-1" style={{ padding: '22px 26px 0' }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: TEXT_MUTED, fontVariantNumeric: 'tabular-nums', marginBottom: 8 }}>
          {t('onboarding.tour.count', { defaultValue: 'How Orella works · {{n}} of {{total}}', n: i + 1, total: steps.length })}
        </div>
        <h1 style={{ fontSize: 27, fontWeight: 800, lineHeight: 1.15, letterSpacing: '-0.025em', color: NAVY, margin: '0 0 10px' }}>
          {s.title}
        </h1>
        <p style={{ fontSize: 16, lineHeight: 1.55, color: '#4A5A6B', margin: 0 }}>{s.body}</p>
      </div>
      <Footer>
        <PrimaryButton variant="blue" onClick={() => (last ? onFinish() : setI(i + 1))}>
          {last ? t('onboarding.tour.finish', 'Get started') : t('onboarding.tour.next', 'Next')}
        </PrimaryButton>
      </Footer>
    </OnboardingShell>
  )
}
